/** O guia das colunas das planilhas de importação: o que vai em cada uma,
 *  pra quem ela vale e se é obrigatória (2026-10-20, pedido da dona: "o
 *  ideal é que nesse pop-up esteja explicando os campos do Excel").
 *
 *  **A lista de colunas espelha o dono do formato** (`TEMPLATE_COLUMNS` em
 *  `core/catalog_import/parsing.py`), na MESMA ordem do modelo que a
 *  pessoa baixa; `tests/test_import_column_guide_parity.py` acusa coluna
 *  que entre num lado e falte no outro. O texto explica a regra que o
 *  servidor aplica; quem recusa a linha continua sendo ele. */

import type { CatalogImportKind } from "@/shared/api/api-client";

export type ColumnGuideRow = {
  column: string;
  /** Pra quem a coluna vale ("Todas", "Veículo", "Canal de rede"). */
  appliesTo: string;
  required: "Sim" | "Não" | string;
  explanation: string;
};

/** Canais e veículos: uma planilha só, e a coluna `Tipo` decide. */
const CATALOG_COLUMNS: readonly ColumnGuideRow[] = [
  {
    column: "Nome",
    appliesTo: "Todas",
    required: "Sim",
    explanation:
      "O nome do canal ou veículo, escrito como ele chega na planilha da clipadora. Nome que já existe é atualizado; nome novo é criado."
  },
  {
    column: "Tipo",
    appliesTo: "Todas",
    required: "Sim",
    explanation:
      "rede (canal de rede social), online ou impresso (imprensa), tv ou radio. É ele que decide se a linha vira canal ou veículo."
  },
  {
    column: "Tier",
    appliesTo: "Veículo",
    required: "No veículo",
    explanation:
      "supertier ou tier1 a tier5 (aceita \"TIER 1\" ou só \"1\"). É a pontuação do veículo no CENA. Linha de rede não leva."
  },
  {
    column: "Peso",
    appliesTo: "Canal de rede",
    required: "No canal",
    explanation:
      "Quanto o canal vale no CENA (aceita vírgula). 0 quer dizer que o canal não pontua. Veículo não leva."
  },
  {
    column: "Apelido de",
    appliesTo: "Todas",
    required: "Não",
    explanation:
      "Preenchida, a linha vira APELIDO: o Nome passa a ser outra grafia do cadastro escrito aqui, e herda tudo dele. Linha de apelido não leva Tier nem Peso. Quando há mais de um veículo com o nome do dono, a UF e o Tipo da linha dizem qual."
  },
  {
    column: "Ignorar",
    appliesTo: "Todas",
    required: "Não",
    explanation:
      "SIM: nada deste canal ou veículo entra. No upload aparece como linha sem menção, não como erro."
  },
  {
    column: "Ignorar verificação de termos",
    appliesTo: "Todas",
    required: "Não",
    explanation:
      "SIM: tudo deste canal ou veículo entra, mesmo sem casar os termos da marca."
  },
  {
    column: "Conteúdo opcional",
    appliesTo: "Veículo",
    required: "Não",
    explanation:
      "SIM pra veículo com paywall: a falha ao buscar o texto da matéria é esperada e não vai pra fila de correção."
  },
  {
    column: "UF",
    appliesTo: "Veículo",
    required: "Não",
    explanation:
      "O estado do veículo. Sigla de duas letras vira maiúscula. Com o Tipo, separa veículos com o mesmo nome (\"Band\" de SP e do RJ)."
  },
  {
    column: "Região",
    appliesTo: "Veículo",
    required: "Não",
    explanation: "Texto livre (ex.: Sudeste)."
  },
  {
    column: "País",
    appliesTo: "Veículo",
    required: "Não",
    explanation: "Texto livre (ex.: Brasil)."
  },
  {
    column: "Preço base",
    appliesTo: "Veículo",
    required: "Não",
    explanation:
      "Em reais, aceita \"1.200,50\". Por enquanto só fica guardado no cadastro: ainda não entra em nenhum cálculo."
  },
  {
    column: "Novo veículo",
    appliesTo: "Veículo",
    required: "Não",
    explanation:
      "SIM: a linha é OUTRO veículo com o mesmo nome de um existente (UF ou Tipo diferentes). Sem ela, um nome que já existe atualiza o veículo."
  },
  {
    column: "Ativo",
    appliesTo: "Todas",
    required: "Não",
    explanation:
      "SIM: reativa o canal ou veículo que estava desativado e atualiza. NÃO: desativa (não apaga). Em branco não mexe: um veículo desativado continua desativado, e a linha volta recusada pedindo pra reativar."
  }
];

/** As notas que valem pra planilha inteira, abaixo da tabela. */
const CATALOG_NOTES: readonly string[] = [
  "Colunas de SIM ou NÃO aceitam também S/N, 1/0 e TRUE/FALSE. Em branco vale NÃO, menos em Ativo, onde em branco não mexe.",
  "Nada é apagado: o que está cadastrado e não está na planilha fica como está. Numa linha que atualiza um veículo, UF, Região, País e Preço base em branco não apagam o que já existe.",
  "Veículo com o mesmo nome em mais de uma linha: cada linha é um veículo, separado pela UF e pelo Tipo. Com vários veículos de mesmo nome já cadastrados, a linha atualiza o que tem a mesma UF e o mesmo Tipo.",
  "Linha com problema é recusada sozinha, e o resto entra. As recusadas saem numa planilha com o motivo em cada linha."
];

/** Publicadores nos grupos: uma linha por PERFIL, e linhas com o mesmo
 *  nome são a mesma pessoa. */
const PUBLISHER_COLUMNS: readonly ColumnGuideRow[] = [
  {
    column: "Nome",
    appliesTo: "Todas",
    required: "Sim",
    explanation:
      "O nome da pessoa. Linhas com o mesmo nome são a MESMA pessoa: cada uma acrescenta o seu perfil e os seus grupos. Pessoa nova é criada; a que já existe só ganha o que faltava."
  },
  {
    column: "Plataforma",
    appliesTo: "Linha com perfil",
    required: "Com a URL",
    explanation:
      "O canal de rede onde está o perfil (Instagram, X…), escrito como no cadastro de Canais. Tem que ser um canal de rede cadastrado: é ele que casa o perfil com as publicações."
  },
  {
    column: "URL do perfil",
    appliesTo: "Linha com perfil",
    required: "Com a plataforma",
    explanation:
      "O link do perfil. Plataforma e URL andam juntas: uma sem a outra recusa a linha. Se a URL já é de OUTRA pessoa, a linha também é recusada e o motivo diz de quem ela é."
  },
  {
    column: "Grupos",
    appliesTo: "Todas",
    required: "Não",
    explanation:
      "Nomes dos grupos de publicador, separados por ponto e vírgula (;). O grupo precisa existir (da empresa ou global): a planilha põe a pessoa EM grupos, não cria grupo."
  }
];

const PUBLISHER_NOTES: readonly string[] = [
  "Plataforma e URL podem ficar as duas vazias: a linha só põe a pessoa nos grupos.",
  "Nada é apagado: o que está cadastrado e não está na planilha fica como está. Repetir um perfil ou um grupo que a pessoa já tem não muda nada.",
  "Linha com problema é recusada sozinha, e o resto entra. As recusadas saem numa planilha com o motivo em cada linha."
];

/** Premissas: a aba `Premissas` da planilha de configuração, sozinha. */
const RULE_COLUMNS: readonly ColumnGuideRow[] = [
  {
    column: "Concorrente",
    appliesTo: "Todas",
    required: "Não",
    explanation:
      "O nome de um concorrente ativo desta empresa. Em branco, a premissa é do cliente. É esta coluna que decide de quem é a linha, não o seletor “Cadastro de” da aba."
  },
  {
    column: "Marca",
    appliesTo: "Todas",
    required: "Sim",
    explanation:
      "O nome da marca (ou da marca do concorrente, se a coluna ao lado estiver preenchida). Ela precisa estar cadastrada e ativa."
  },
  {
    column: "Tarefa",
    appliesTo: "Todas",
    required: "Sim",
    explanation:
      "Onde a premissa entra, pelo nome que a tela usa: Polaridade e intensidade, Papel da marca (abordagem), Subtemas, Mensagem-chave, Citação no título… A lista completa está na planilha de “Baixar as premissas atuais”."
  },
  {
    column: "Fonte",
    appliesTo: "Todas",
    required: "Sim",
    explanation: "imprensa ou redes: o que vale numa fonte não vale na outra."
  },
  {
    column: "Tipo de regra",
    appliesTo: "Todas",
    required: "Sim",
    explanation:
      "A seção do prompt, pelo nome da tela: Positivo, Negativo, Favorável, Desfavorável, Neutro, Extra positivo, Protagonista, Coadjuvante, Citação, Global… Os mesmos nomes saem na planilha de premissas atuais."
  },
  {
    column: "Posição",
    appliesTo: "Todas",
    required: "Não",
    explanation:
      "A posição que já existe na seção ATUALIZA aquela premissa. Em branco, fora da faixa da seção ou já ocupada por outro texto, a premissa entra na próxima posição livre."
  },
  {
    column: "Texto",
    appliesTo: "Todas",
    required: "Sim",
    explanation:
      "O texto da premissa, como vai pro prompt. Em branco a linha é recusada."
  },
  {
    column: "Ativo",
    appliesTo: "Todas",
    required: "Não",
    explanation:
      "Escreva NÃO pra desativar a premissa (ela continua cadastrada, só deixa de valer) e SIM pra reativar. Em branco vale SIM: célula esquecida não desativa nada."
  }
];

const RULE_NOTES: readonly string[] = [
  "Cada seção numerada aceita no máximo 15 premissas ATIVAS. A 16ª é recusada com o motivo: desative uma antes de acrescentar outra.",
  "Nada é apagado: o que está cadastrado e não está na planilha fica como está.",
  "A chave que libera a edição manual de prompts não vale aqui: a importação é de Especialista.",
  "Linha com problema é recusada sozinha, e o resto entra. As recusadas saem numa planilha com o motivo em cada linha."
];

/** Temas e subtemas: duas abas na mesma planilha (a `Temas` e a
 *  `Subtemas` da configuração), cada uma com a sua tabela. */
const THEME_GROUP_COLUMNS: readonly ColumnGuideRow[] = [
  {
    column: "Concorrente",
    appliesTo: "Todas",
    required: "Não",
    explanation:
      "O nome de um concorrente ativo desta empresa. Em branco, o tema é do cliente. Concorrente que não existe ativo recusa a linha."
  },
  {
    column: "Nome",
    appliesTo: "Todas",
    required: "Sim",
    explanation:
      "O nome do tema. Nome que já existe (sem diferença de maiúscula ou acento) é atualizado; nome novo é criado. Se houver dois temas ativos com o mesmo nome, a linha é recusada."
  },
  {
    column: "Peso",
    appliesTo: "Todas",
    required: "Não",
    explanation:
      "Multiplicador do tema no cálculo (número, aceita vírgula; não pode ser negativo). Em branco é sem peso próprio — e limpa o peso de um tema que já tinha."
  },
  {
    column: "Ativo",
    appliesTo: "Todas",
    required: "Não",
    explanation:
      "Escreva NÃO pra desativar o tema (ou criá-lo já desativado) e SIM pra reativar. Em branco vale SIM: célula esquecida não desativa nada."
  }
];

const THEME_COLUMNS: readonly ColumnGuideRow[] = [
  {
    column: "Concorrente",
    appliesTo: "Todas",
    required: "Não",
    explanation:
      "O nome de um concorrente ativo desta empresa. Em branco, o subtema é do cliente. Marca e Tema são procurados no cadastro de quem a linha é."
  },
  {
    column: "Marca",
    appliesTo: "Todas",
    required: "Sim",
    explanation:
      "A marca do subtema (a do concorrente, se a coluna ao lado estiver preenchida). Ela precisa existir ativa — a não ser que a linha tenha Ativo = NÃO."
  },
  {
    column: "Tema",
    appliesTo: "Todas",
    required: "Sim",
    explanation:
      "O tema a que o subtema pertence. Ele precisa existir ativo ou vir na aba Temas do mesmo arquivo, que é aplicada antes. Se o subtema já existe na marca e a linha cita outro tema, ele muda de tema."
  },
  {
    column: "Nome",
    appliesTo: "Todas",
    required: "Sim",
    explanation:
      "O nome do subtema. Dentro de cada marca, nome que já existe é atualizado e nome novo é criado. Dois subtemas ativos com o mesmo nome na marca recusam a linha."
  },
  {
    column: "Termos",
    appliesTo: "Todas",
    required: "No só determinístico",
    explanation:
      "Expressões separadas por ponto e vírgula (;), cada uma com AND, OR, NOT e aspas — basta uma casar. Expressão inválida recusa a linha. Em branco, só a IA identifica (e apaga os termos de um subtema que já tinha)."
  },
  {
    column: "Classificação",
    appliesTo: "Todas",
    required: "Não",
    explanation:
      "só determinístico, só IA ou determinístico e IA. Em branco vale determinístico e IA. “Só determinístico” sem Termos recusa a linha: ele nunca seria atribuído."
  },
  {
    column: "Determinístico em",
    appliesTo: "Modo os dois",
    required: "Não",
    explanation:
      "Em que fonte o termo vale: imprensa, redes ou as duas (em branco vale as duas). Só conta em determinístico e IA; nos outros modos a célula é ignorada."
  },
  {
    column: "Premissas de detecção",
    appliesTo: "Todas",
    required: "Não",
    explanation:
      "Texto livre que entra no prompt da IA: quando identificar o subtema. Não é expressão booleana. Em branco apaga o que já estava."
  },
  {
    column: "Premissas de não detecção",
    appliesTo: "Todas",
    required: "Não",
    explanation:
      "Texto livre que entra no prompt da IA: quando NÃO identificar o subtema. Em branco apaga o que já estava."
  },
  {
    column: "Ativo",
    appliesTo: "Todas",
    required: "Não",
    explanation:
      "Escreva NÃO pra desativar o subtema (ou criá-lo já desativado) e SIM pra reativar. Em branco vale SIM. Só linha ativa exige marca e tema ativos."
  }
];

const THEME_NOTES: readonly string[] = [
  "Nada é apagado: o que está cadastrado e não está na planilha fica como está. Em linha que atualiza, célula em branco limpa o campo (Peso, Termos e as premissas).",
  "A planilha de antes da troca de nomes também entra: aba “Grupos de tema” (agora Temas), aba “Temas” com a coluna Grupo (agora Subtemas, com a coluna Tema) e a coluna antiga “Determinístico estrito” (SIM vale só determinístico).",
  "O tema do cliente e o de cada concorrente são cadastros separados: a mesma planilha pode trazer os dois, pela coluna Concorrente.",
  "Linha com problema é recusada sozinha, e o resto entra. As recusadas saem numa planilha com o motivo em cada linha."
];

/** Um bloco do guia: uma tabela de colunas, com título quando a planilha
 *  tem mais de uma aba. */
export type ColumnGuideSection = {
  title: string;
  columns: readonly ColumnGuideRow[];
};

export const IMPORT_COLUMN_GUIDE: Partial<
  Record<
    CatalogImportKind,
    { sections: readonly ColumnGuideSection[]; notes: readonly string[] }
  >
> = {
  catalog: { sections: [{ title: "", columns: CATALOG_COLUMNS }], notes: CATALOG_NOTES },
  publishers: { sections: [{ title: "", columns: PUBLISHER_COLUMNS }], notes: PUBLISHER_NOTES },
  rules: { sections: [{ title: "", columns: RULE_COLUMNS }], notes: RULE_NOTES },
  themes: {
    sections: [
      { title: "Aba Temas", columns: THEME_GROUP_COLUMNS },
      { title: "Aba Subtemas", columns: THEME_COLUMNS }
    ],
    notes: THEME_NOTES
  }
};

export function ImportColumnGuide({ kind }: { kind: CatalogImportKind }) {
  const guide = IMPORT_COLUMN_GUIDE[kind];
  if (!guide) return null;
  return (
    <section className="import-column-guide" aria-label="Colunas da planilha">
      <h4>Colunas da planilha</h4>
      {guide.sections.map(section => (
        <div key={section.title || "colunas"} className="import-column-section">
          {section.title && <h5 className="import-column-section-title">{section.title}</h5>}
          <div className="import-column-table-wrap">
            <table className="import-column-table" aria-label={section.title || "Colunas"}>
              <thead>
                <tr>
                  <th scope="col">Coluna</th>
                  <th scope="col">Vale pra</th>
                  <th scope="col">Obrigatória</th>
                  <th scope="col">O que pôr</th>
                </tr>
              </thead>
              <tbody>
                {section.columns.map(row => (
                  <tr key={row.column}>
                    <th scope="row">{row.column}</th>
                    <td>{row.appliesTo}</td>
                    <td>{row.required}</td>
                    <td>{row.explanation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      <ul className="import-column-notes">
        {guide.notes.map(note => (
          <li key={note}>{note}</li>
        ))}
      </ul>
    </section>
  );
}
